/**
 * Symbology Types and Interfaces
 *
 * Defines all symbology-related TypeScript interfaces, types, type guards,
 * and default constants for the GeoVera WebGIS Platform.
 *
 * Supports:
 * - Simple symbology for Point, LineString, and Polygon layers
 * - Categorized symbology (unique value rendering)
 * - Graduated symbology (range-based rendering)
 * - Feature labeling with style customization
 *
 * **Validates: Requirements 6, 41, 42, 43** - Layer Symbology Modification & Advanced Symbology
 */

// ---------------------------------------------------------------------------
// Core symbol style
// ---------------------------------------------------------------------------

/**
 * Low-level style properties shared across all symbol types.
 * Used as the per-rule symbol in categorized and graduated renderers.
 */
export interface SymbolStyle {
  /** Fill color as a hex string (e.g. '#3388ff'). Applies to polygons and point markers. */
  fillColor?: string;
  /** Stroke/outline color as a hex string. */
  strokeColor?: string;
  /** Stroke width in pixels. Must be >= 0. */
  strokeWidth?: number;
  /** Opacity in the range [0, 1]. 0 = fully transparent, 1 = fully opaque. */
  opacity?: number;
  /** Marker/symbol size in pixels. Applies to point features. Must be > 0. */
  size?: number;
}

// ---------------------------------------------------------------------------
// Categorized and graduated rule types
// ---------------------------------------------------------------------------

/**
 * A single rule in a categorized (unique-value) renderer.
 * Features whose `attributeField` value equals `value` are drawn with `symbol`.
 */
export interface CategorizedRule {
  /** The attribute field used for classification. */
  attributeField: string;
  /** The attribute value that triggers this rule. */
  value: unknown;
  /** The symbol style applied to matching features. */
  symbol: SymbolStyle;
  /** Human-readable label shown in the legend. */
  label: string;
}

/**
 * A single rule in a graduated (range-based) renderer.
 * Features whose numeric `attributeField` value falls in [minValue, maxValue]
 * are drawn with `symbol`.
 */
export interface GraduatedRule {
  /** The numeric attribute field used for classification. */
  attributeField: string;
  /** Lower bound of the value range (inclusive). */
  minValue: number;
  /** Upper bound of the value range (inclusive). */
  maxValue: number;
  /** The symbol style applied to features in this range. */
  symbol: SymbolStyle;
  /** Human-readable label shown in the legend (e.g. '0 – 100'). */
  label: string;
}

// ---------------------------------------------------------------------------
// Base Symbology interface
// ---------------------------------------------------------------------------

/**
 * Complete symbology configuration for a vector layer.
 *
 * - `type: 'simple'`      — all features share the same style
 * - `type: 'categorized'` — features are styled by unique attribute values
 * - `type: 'graduated'`   — features are styled by numeric attribute ranges
 */
export interface Symbology {
  /** Renderer type. */
  type: 'simple' | 'categorized' | 'graduated';

  // --- Simple renderer fields ---

  /** Fill color as a hex string. Used for polygons and point markers. */
  fillColor?: string;
  /** Stroke/outline color as a hex string. */
  strokeColor?: string;
  /** Stroke width in pixels. Must be >= 0. */
  strokeWidth?: number;
  /** Layer/feature opacity in the range [0, 1]. */
  opacity?: number;

  // --- Point-specific fields ---

  /**
   * Marker symbol identifier.
   * Supported values: 'circle' | 'square' | 'triangle' | 'star' | 'cross' | 'x'
   */
  markerSymbol?: MarkerSymbol;
  /** Marker radius/size in pixels. Must be > 0. */
  markerSize?: number;

  // --- Advanced renderer fields ---

  /** Rules for categorized (unique-value) rendering. */
  categorizedRules?: CategorizedRule[];
  /** Rules for graduated (range-based) rendering. */
  graduatedRules?: GraduatedRule[];
  /** Label configuration for feature labeling. */
  labelConfig?: LabelConfig;
  /** Category groups for grouping multiple values under one symbol. */
  categoryGroups?: CategoryGroup[];
}

// ---------------------------------------------------------------------------
// Marker symbol type
// ---------------------------------------------------------------------------

/** Supported point marker symbol identifiers. */
export type MarkerSymbol = 'circle' | 'square' | 'triangle' | 'star' | 'cross' | 'x';

// ---------------------------------------------------------------------------
// Classification method type
// ---------------------------------------------------------------------------

/** Supported classification methods for graduated symbology. */
export type ClassificationMethod =
  | 'equal-interval'
  | 'quantile'
  | 'natural-breaks'
  | 'standard-deviation';

// ---------------------------------------------------------------------------
// Color ramp types
// ---------------------------------------------------------------------------

/** A color ramp definition with a name and array of hex color stops. */
export interface ColorRamp {
  /** Unique identifier for the color ramp. */
  id: string;
  /** Human-readable name. */
  name: string;
  /** Type of color ramp. */
  type: 'sequential' | 'diverging';
  /** Array of hex color stops from low to high. */
  colors: string[];
}

// ---------------------------------------------------------------------------
// Label configuration
// ---------------------------------------------------------------------------

/** Label placement strategy. */
export type LabelPlacement = 'point' | 'line' | 'centroid';

/**
 * Configuration for feature labeling.
 * **Validates: Requirement 43** - Feature Labeling
 */
export interface LabelConfig {
  /** Whether labeling is enabled. */
  enabled: boolean;
  /** The attribute field to use for label text. */
  field: string;
  /** Font family (e.g. 'Arial', 'sans-serif'). */
  font: string;
  /** Font size in pixels. */
  fontSize: number;
  /** Label text color as hex string. */
  color: string;
  /** Halo (text outline) color as hex string. */
  haloColor?: string;
  /** Halo width in pixels. */
  haloWidth?: number;
  /** Label placement strategy. */
  placement: LabelPlacement;
  /** Expression for combining multiple fields (e.g. '{name} - {type}'). */
  expression?: string;
  /** Whether to use declutter to avoid label overlaps. */
  declutter?: boolean;
}

// ---------------------------------------------------------------------------
// Category group
// ---------------------------------------------------------------------------

/**
 * A category group that assigns the same symbol to multiple values.
 * **Validates: Requirement 41.6**
 */
export interface CategoryGroup {
  /** Human-readable group name. */
  name: string;
  /** The values that belong to this group. */
  values: unknown[];
  /** The symbol style applied to all values in this group. */
  symbol: SymbolStyle;
}

// ---------------------------------------------------------------------------
// Geometry-type-specific symbology interfaces
// ---------------------------------------------------------------------------

/**
 * Symbology for Polygon layers.
 * Requires fill color, stroke color, stroke width, and opacity.
 * Requirement 6.3: fill color, stroke color, stroke width, opacity.
 */
export interface PolygonSymbology extends Symbology {
  /** Fill color is required for polygon layers. */
  fillColor: string;
  /** Stroke color is required for polygon layers. */
  strokeColor: string;
  /** Stroke width is required for polygon layers. */
  strokeWidth: number;
  /** Opacity is required for polygon layers. */
  opacity: number;
}

/**
 * Symbology for LineString layers.
 * Requires stroke color, stroke width, and opacity.
 * Requirement 6.4: stroke color, stroke width, opacity.
 */
export interface LineSymbology extends Symbology {
  /** Stroke color is required for line layers. */
  strokeColor: string;
  /** Stroke width is required for line layers. */
  strokeWidth: number;
  /** Opacity is required for line layers. */
  opacity: number;
}

/**
 * Symbology for Point layers.
 * Requires marker symbol, marker size, fill color (marker color), and opacity.
 * Requirement 6.5: marker symbol, size, color, opacity.
 */
export interface PointSymbology extends Symbology {
  /** Marker symbol identifier. */
  markerSymbol: MarkerSymbol;
  /** Marker size in pixels. */
  markerSize: number;
  /** Marker fill color. */
  fillColor: string;
  /** Opacity is required for point layers. */
  opacity: number;
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

/**
 * Returns true if `value` is a valid Symbology object.
 */
export function isSymbology(value: unknown): value is Symbology {
  if (!value || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  return s.type === 'simple' || s.type === 'categorized' || s.type === 'graduated';
}

/**
 * Returns true if `symbology` is a PolygonSymbology (has all required polygon fields).
 */
export function isPolygonSymbology(symbology: Symbology): symbology is PolygonSymbology {
  return (
    typeof symbology.fillColor === 'string' &&
    typeof symbology.strokeColor === 'string' &&
    typeof symbology.strokeWidth === 'number' &&
    typeof symbology.opacity === 'number'
  );
}

/**
 * Returns true if `symbology` is a LineSymbology (has all required line fields).
 */
export function isLineSymbology(symbology: Symbology): symbology is LineSymbology {
  return (
    typeof symbology.strokeColor === 'string' &&
    typeof symbology.strokeWidth === 'number' &&
    typeof symbology.opacity === 'number'
  );
}

/**
 * Returns true if `symbology` is a PointSymbology (has all required point fields).
 */
export function isPointSymbology(symbology: Symbology): symbology is PointSymbology {
  return (
    typeof symbology.markerSymbol === 'string' &&
    typeof symbology.markerSize === 'number' &&
    typeof symbology.fillColor === 'string' &&
    typeof symbology.opacity === 'number'
  );
}

/**
 * Returns true if `symbology` uses the categorized renderer.
 */
export function isCategorizedSymbology(
  symbology: Symbology
): symbology is Symbology & { categorizedRules: CategorizedRule[] } {
  return symbology.type === 'categorized' && Array.isArray(symbology.categorizedRules);
}

/**
 * Returns true if `symbology` uses the graduated renderer.
 */
export function isGraduatedSymbology(
  symbology: Symbology
): symbology is Symbology & { graduatedRules: GraduatedRule[] } {
  return symbology.type === 'graduated' && Array.isArray(symbology.graduatedRules);
}

// ---------------------------------------------------------------------------
// Default symbology constants
// ---------------------------------------------------------------------------

/**
 * Default symbology for Polygon layers.
 * Blue fill, white stroke, 2px width, fully opaque.
 */
export const DEFAULT_POLYGON_SYMBOLOGY: PolygonSymbology = {
  type: 'simple',
  fillColor: '#3388ff',
  strokeColor: '#ffffff',
  strokeWidth: 2,
  opacity: 1,
};

/**
 * Default symbology for LineString layers.
 * Blue stroke, 3px width, fully opaque.
 */
export const DEFAULT_LINE_SYMBOLOGY: LineSymbology = {
  type: 'simple',
  strokeColor: '#3388ff',
  strokeWidth: 3,
  opacity: 1,
};

/**
 * Default symbology for Point layers.
 * Circle marker, 8px radius, blue fill, fully opaque.
 */
export const DEFAULT_POINT_SYMBOLOGY: PointSymbology = {
  type: 'simple',
  markerSymbol: 'circle',
  markerSize: 8,
  fillColor: '#3388ff',
  strokeColor: '#ffffff',
  strokeWidth: 2,
  opacity: 1,
};

/**
 * Returns the appropriate default symbology for a given geometry type.
 *
 * @param geometryType - The GeoJSON geometry type of the layer
 * @returns The default Symbology for that geometry type
 */
export function getDefaultSymbology(
  geometryType: 'Point' | 'LineString' | 'Polygon'
): PolygonSymbology | LineSymbology | PointSymbology {
  switch (geometryType) {
    case 'Polygon':
      return { ...DEFAULT_POLYGON_SYMBOLOGY };
    case 'LineString':
      return { ...DEFAULT_LINE_SYMBOLOGY };
    case 'Point':
      return { ...DEFAULT_POINT_SYMBOLOGY };
  }
}

// ---------------------------------------------------------------------------
// Legend item interface
// ---------------------------------------------------------------------------

/**
 * A single legend entry for display in the map legend.
 */
export interface LegendItem {
  /** Human-readable label for this legend entry. */
  label: string;
  /** The symbol style for this legend entry. */
  symbol: SymbolStyle;
}

// ---------------------------------------------------------------------------
// Default label configuration
// ---------------------------------------------------------------------------

/**
 * Default label configuration (labeling disabled).
 */
export const DEFAULT_LABEL_CONFIG: LabelConfig = {
  enabled: false,
  field: '',
  font: 'Arial',
  fontSize: 12,
  color: '#000000',
  haloColor: '#ffffff',
  haloWidth: 2,
  placement: 'point',
  declutter: true,
};

// ---------------------------------------------------------------------------
// Predefined color ramps
// ---------------------------------------------------------------------------

/**
 * Predefined color ramps for graduated symbology.
 * Includes sequential and diverging ramps.
 */
export const PREDEFINED_COLOR_RAMPS: ColorRamp[] = [
  {
    id: 'blues',
    name: 'Blues',
    type: 'sequential',
    colors: [
      '#f7fbff',
      '#deebf7',
      '#c6dbef',
      '#9ecae1',
      '#6baed6',
      '#4292c6',
      '#2171b5',
      '#084594',
    ],
  },
  {
    id: 'reds',
    name: 'Reds',
    type: 'sequential',
    colors: [
      '#fff5f0',
      '#fee0d2',
      '#fcbba1',
      '#fc9272',
      '#fb6a4a',
      '#ef3b2c',
      '#cb181d',
      '#99000d',
    ],
  },
  {
    id: 'greens',
    name: 'Greens',
    type: 'sequential',
    colors: [
      '#f7fcf5',
      '#e5f5e0',
      '#c7e9c0',
      '#a1d99b',
      '#74c476',
      '#41ab5d',
      '#238b45',
      '#005a32',
    ],
  },
  {
    id: 'oranges',
    name: 'Oranges',
    type: 'sequential',
    colors: [
      '#fff5eb',
      '#fee6ce',
      '#fdd0a2',
      '#fdae6b',
      '#fd8d3c',
      '#f16913',
      '#d94801',
      '#8c2d04',
    ],
  },
  {
    id: 'purples',
    name: 'Purples',
    type: 'sequential',
    colors: [
      '#fcfbfd',
      '#efedf5',
      '#dadaeb',
      '#bcbddc',
      '#9e9ac8',
      '#807dba',
      '#6a51a3',
      '#4a1486',
    ],
  },
  {
    id: 'ylgnbu',
    name: 'Yellow-Green-Blue',
    type: 'sequential',
    colors: [
      '#ffffd9',
      '#edf8b1',
      '#c7e9b4',
      '#7fcdbb',
      '#41b6c4',
      '#1d91c0',
      '#225ea8',
      '#0c2c84',
    ],
  },
  {
    id: 'rdylgn',
    name: 'Red-Yellow-Green',
    type: 'diverging',
    colors: [
      '#d73027',
      '#f46d43',
      '#fdae61',
      '#fee08b',
      '#d9ef8b',
      '#a6d96a',
      '#66bd63',
      '#1a9850',
    ],
  },
  {
    id: 'rdbu',
    name: 'Red-Blue',
    type: 'diverging',
    colors: [
      '#b2182b',
      '#d6604d',
      '#f4a582',
      '#fddbc7',
      '#d1e5f0',
      '#92c5de',
      '#4393c3',
      '#2166ac',
    ],
  },
  {
    id: 'spectral',
    name: 'Spectral',
    type: 'diverging',
    colors: [
      '#d53e4f',
      '#f46d43',
      '#fdae61',
      '#fee08b',
      '#e6f598',
      '#abdda4',
      '#66c2a5',
      '#3288bd',
    ],
  },
  {
    id: 'viridis',
    name: 'Viridis',
    type: 'sequential',
    colors: [
      '#440154',
      '#482777',
      '#3e4989',
      '#31688e',
      '#26828e',
      '#1f9e89',
      '#35b779',
      '#6ece58',
    ],
  },
];
